import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import './ClodeAssistant.css';

type AssistantMode = 'idle' | 'guiding' | 'thinking' | 'success' | 'warning';

interface TourStep {
  selector: string;
  title: string;
  body: string;
}

interface PageContext {
  title: string;
  summary: string;
  nextBestActions: string[];
}

interface FocusRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface AssistantAnchor {
  left: number;
  top: number;
}

interface AgentMessage {
  role: 'user' | 'assistant';
  content: string;
}

const serviceLabels: Record<string, string> = {
  '/services/cloud-services': 'Cloud Services',
  '/services/cybersecurity': 'Cybersecurity',
  '/services/managed-services': 'Managed Services',
  '/services/devops-automation': 'DevOps & Automation',
  '/services/data-analytics': 'Data Analytics',
  '/services/backup-recovery': 'Backup & Recovery',
  '/services/development': 'Development',
  '/services/enterprise-learning': 'Enterprise Learning',
};

const homeTour: TourStep[] = [
  {
    selector: '[data-clode-target="home-hero"]',
    title: 'CloudYatra value proposition',
    body: 'Start here when evaluating CloudYatra. The hero routes visitors toward consultation or service discovery.',
  },
  {
    selector: '[data-clode-target="home-primary-cta"]',
    title: 'Fastest conversion path',
    body: 'Use this action when the visitor already wants to speak with the team or book a discovery call.',
  },
  {
    selector: '[data-clode-target="home-capabilities"]',
    title: 'Service capability map',
    body: 'This section explains the main operating areas: cloud, security, AI analytics, DevOps, and managed services.',
  },
  {
    selector: '[data-clode-target="home-impact"]',
    title: 'Proof and confidence',
    body: 'Use these metrics as trust support after the visitor understands the services.',
  },
];

const serviceTour: TourStep[] = [
  {
    selector: '[data-clode-target="service-hero"]',
    title: 'Service overview',
    body: 'The hero summarizes the service outcome and sets context before the visitor reviews details.',
  },
  {
    selector: '[data-clode-target="service-features"]',
    title: 'Technical scope',
    body: 'The feature list breaks down what CloudYatra can deliver for this service line.',
  },
  {
    selector: '[data-clode-target="service-benefits"]',
    title: 'Business value',
    body: 'The benefits section connects technical work to operating value such as cost, resilience, speed, and support.',
  },
  {
    selector: '[data-clode-target="service-cta"]',
    title: 'Consultation handoff',
    body: 'When the visitor is ready, this action moves them to the contact flow.',
  },
];

const contactTour: TourStep[] = [
  {
    selector: '[data-clode-target="contact-intro"]',
    title: 'Contact entry point',
    body: 'This section confirms the visitor is in the right place for a consultation or support request.',
  },
  {
    selector: '[data-clode-target="contact-form"]',
    title: 'Request details',
    body: 'The form captures service interest and enough context for CloudYatra to route the request correctly.',
  },
  {
    selector: '[data-clode-target="contact-info"]',
    title: 'Direct channels',
    body: 'Use these channels when the visitor prefers email, phone, or social contact instead of the form.',
  },
];

const defaultTour: TourStep[] = [
  {
    selector: '[data-clode-target="primary-navigation"]',
    title: 'Navigation',
    body: 'Use the main navigation to move between CloudYatra services, resources, company details, and contact.',
  },
  {
    selector: '#main-content',
    title: 'Current page',
    body: 'This is the main page content. Clode can add more guided steps as this section gets page-specific targets.',
  },
];

const getTourSteps = (pathname: string): TourStep[] => {
  if (pathname === '/') {
    return homeTour;
  }

  if (pathname === '/contact') {
    return contactTour;
  }

  if (pathname.startsWith('/services/')) {
    return serviceTour;
  }

  return defaultTour;
};

const getPageContext = (pathname: string): PageContext => {
  if (pathname === '/') {
    return {
      title: 'Home',
      summary: 'Guide visitors from CloudYatra positioning to service discovery or consultation.',
      nextBestActions: ['Explain services', 'Route to consultation', 'Show trust signals'],
    };
  }

  if (pathname === '/contact') {
    return {
      title: 'Contact',
      summary: 'Help visitors submit a complete consultation request or use direct contact channels.',
      nextBestActions: ['Review required fields', 'Choose service interest', 'Submit request'],
    };
  }

  if (pathname.startsWith('/services/')) {
    return {
      title: serviceLabels[pathname] || 'Service',
      summary: 'Help visitors understand scope, business benefits, and when to speak with the team.',
      nextBestActions: ['Scan features', 'Review benefits', 'Contact CloudYatra'],
    };
  }

  if (pathname === '/about') {
    return {
      title: 'About',
      summary: 'Orient visitors around CloudYatra company context, credibility, and expertise.',
      nextBestActions: ['Review company story', 'Check expertise', 'Contact team'],
    };
  }

  if (pathname === '/resources') {
    return {
      title: 'Resources',
      summary: 'Help visitors find learning material and service guidance.',
      nextBestActions: ['Browse resources', 'Compare service areas', 'Ask for consultation'],
    };
  }

  return {
    title: 'CloudYatra',
    summary: 'Provide lightweight guidance for the current page.',
    nextBestActions: ['Review page', 'Explore services', 'Contact team'],
  };
};

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

const getReducedMotionPreference = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return Boolean(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches);
};

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getReducedMotionPreference);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!mediaQuery) {
      return undefined;
    }
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  return prefersReducedMotion;
};

const ClodeGlyph: React.FC<{ mode: AssistantMode; compact?: boolean }> = ({ mode, compact = false }) => {
  return (
    <div className={`clode-glyph clode-glyph--${mode} ${compact ? 'clode-glyph--compact' : ''}`} aria-hidden="true">
      <span className="clode-glyph__lobe clode-glyph__lobe--left" />
      <span className="clode-glyph__lobe clode-glyph__lobe--center" />
      <span className="clode-glyph__lobe clode-glyph__lobe--right" />
      <span className="clode-glyph__base" />
      <span className="clode-glyph__eye clode-glyph__eye--left" />
      <span className="clode-glyph__eye clode-glyph__eye--right" />
      <span className="clode-glyph__terminal">&gt;_</span>
      <span className="clode-glyph__signal" />
    </div>
  );
};

const ClodeAssistant: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [tourIndex, setTourIndex] = useState<number | null>(null);
  const [focusRect, setFocusRect] = useState<FocusRect | null>(null);
  const [assistantAnchor, setAssistantAnchor] = useState<AssistantAnchor | null>(null);
  const [mode, setMode] = useState<AssistantMode>('idle');
  const [agentMessages, setAgentMessages] = useState<AgentMessage[]>([]);
  const [agentInput, setAgentInput] = useState('');
  const [agentError, setAgentError] = useState<string | null>(null);
  const [isAgentThinking, setIsAgentThinking] = useState(false);
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    const checkConfiguration = async () => {
      // Probe the Vercel serverless endpoint
      try {
        const response = await fetch('/api/clode-agent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: [] }),
        });
        // 400 = server is live but rejected empty messages → configured
        setIsConfigured(response.status === 400 || response.ok);
      } catch {
        setIsConfigured(false);
      }
    };
    checkConfiguration();
  }, []);


  const pageContext = useMemo(() => getPageContext(location.pathname), [location.pathname]);
  const tourSteps = useMemo(() => getTourSteps(location.pathname), [location.pathname]);
  const activeStep = tourIndex !== null ? tourSteps[tourIndex] : null;
  const isTouring = activeStep !== null;

  const stopTour = useCallback(() => {
    setTourIndex(null);
    setFocusRect(null);
    setAssistantAnchor(null);
    setMode('idle');
  }, []);

  useEffect(() => {
    stopTour();
  }, [location.pathname, stopTour]);

  useEffect(() => {
    if (!isOpen && !isTouring) {
      setMode('idle');
    }
  }, [isOpen, isTouring]);

  const updateTourPosition = useCallback(() => {
    if (!activeStep) {
      return;
    }

    const target = document.querySelector<HTMLElement>(activeStep.selector);

    if (!target) {
      setFocusRect(null);
      setAssistantAnchor(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    const padding = 10;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    setFocusRect({
      left: clamp(rect.left - padding, 8, viewportWidth - 16),
      top: clamp(rect.top - padding, 8, viewportHeight - 16),
      width: clamp(rect.width + padding * 2, 24, viewportWidth - 16),
      height: clamp(rect.height + padding * 2, 24, viewportHeight - 16),
    });

    if (viewportWidth < 760) {
      setAssistantAnchor(null);
      return;
    }

    const assistantWidth = 380;
    const assistantHeight = 330;
    const gap = 18;
    const rightSide = rect.right + gap;
    const left = rightSide + assistantWidth < viewportWidth - 18
      ? rightSide
      : rect.left - assistantWidth - gap;

    setAssistantAnchor({
      left: clamp(left, 18, viewportWidth - assistantWidth - 18),
      top: clamp(rect.top + rect.height / 2 - assistantHeight / 2, 82, viewportHeight - assistantHeight - 18),
    });
  }, [activeStep]);

  useEffect(() => {
    if (!activeStep) {
      return undefined;
    }

    const target = document.querySelector<HTMLElement>(activeStep.selector);
    target?.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });

    const timer = window.setTimeout(updateTourPosition, prefersReducedMotion ? 0 : 260);
    window.addEventListener('resize', updateTourPosition);
    window.addEventListener('scroll', updateTourPosition, { passive: true });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', updateTourPosition);
      window.removeEventListener('scroll', updateTourPosition);
    };
  }, [activeStep, prefersReducedMotion, updateTourPosition]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        stopTour();
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [stopTour]);

  const startTour = () => {
    setIsMinimized(false);
    setIsOpen(true);
    setMode('guiding');
    setTourIndex(0);
  };

  const goToNextStep = () => {
    if (tourIndex === null) {
      return;
    }

    if (tourIndex >= tourSteps.length - 1) {
      setMode('success');
      window.setTimeout(() => setMode('idle'), 1200);
      stopTour();
      return;
    }

    setTourIndex(tourIndex + 1);
  };

  const goToPreviousStep = () => {
    if (tourIndex === null || tourIndex === 0) {
      return;
    }

    setTourIndex(tourIndex - 1);
  };

  const handleNavigate = (path: string) => {
    stopTour();
    setIsOpen(false);
    navigate(path);
  };

  const sendAgentMessage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedInput = agentInput.trim();

    if (!trimmedInput || isAgentThinking) {
      return;
    }

    const nextMessages: AgentMessage[] = [
      ...agentMessages,
      {
        role: 'user' as const,
        content: trimmedInput,
      },
    ].slice(-10);

    setAgentInput('');
    setAgentMessages(nextMessages);
    setAgentError(null);
    setIsAgentThinking(true);
    setMode('thinking');

    try {
      let response: Response;
      let result: Record<string, any>;

      // ── Server-side proxy route (Vercel serverless) ─────────────
      response = await fetch('/api/clode-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: nextMessages,
          pageContext: { title: pageContext.title, path: location.pathname },
        }),
      });

      // Special handling for local dev 404
      if (response.status === 404) {
        throw new Error('Clode agent endpoint not found. Please run "npm run dev:agent" to enable AI features locally.');
      }

      result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.error?.message || result?.error || 'Clode agent is unavailable.');
      }

      const assistantMessage = result?.message?.content;

      if (!assistantMessage) {
        throw new Error('Clode returned an empty answer.');
      }

      setAgentMessages([
        ...nextMessages,
        { role: 'assistant' as const, content: assistantMessage },
      ].slice(-10));

      setMode('success');
      window.setTimeout(() => setMode('idle'), 900);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Clode agent request failed.';
      setAgentError(message);
      setMode('warning');
    } finally {
      setIsAgentThinking(false);
    }
  };

  const panelVariants = {
    hidden: { opacity: 0, y: 18, scale: 0.96 },
    visible: { opacity: 1, y: 0, scale: 1 },
  };

  const rootStyle: React.CSSProperties | undefined = isTouring && assistantAnchor
    ? {
        left: assistantAnchor.left,
        top: assistantAnchor.top,
        right: 'auto',
        bottom: 'auto',
      }
    : undefined;

  return (
    <>
      <AnimatePresence>
        {focusRect && (
          <motion.div
            className="clode-focus-ring"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              left: focusRect.left,
              top: focusRect.top,
              width: focusRect.width,
              height: focusRect.height,
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.22 }}
          />
        )}
      </AnimatePresence>

      <motion.aside
        className={`clode-assistant ${isTouring ? 'clode-assistant--touring' : ''}`}
        style={rootStyle}
        aria-label="Clode assistant"
        layout={!prefersReducedMotion}
      >
        <AnimatePresence>
          {isOpen && !isMinimized && (
            <motion.section
              className="clode-panel"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={panelVariants}
              transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
              role="dialog"
              aria-label="Clode guidance panel"
            >
              <header className="clode-panel__header">
                <div className="clode-panel__identity">
                  <ClodeGlyph mode={mode} compact />
                  <div>
                    <p className="clode-panel__eyebrow">CloudYatra assistant</p>
                    <h2>Clode</h2>
                  </div>
                </div>
                <div className="clode-panel__controls">
                  <button
                    type="button"
                    className="clode-icon-button"
                    onClick={() => setIsMinimized(true)}
                    aria-label="Minimize Clode"
                  >
                    <i className="bi bi-dash-lg" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="clode-icon-button"
                    onClick={() => {
                      stopTour();
                      setIsOpen(false);
                    }}
                    aria-label="Close Clode"
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </div>
              </header>

              <div className="clode-status" aria-live="polite">
                <span className={`clode-status__dot clode-status__dot--${mode}`} />
                {isTouring ? `Guiding step ${tourIndex! + 1} of ${tourSteps.length}` : 'Ready for contextual guidance'}
              </div>

              {activeStep ? (
                <div className="clode-tour-copy">
                  <p className="clode-panel__eyebrow">Page guide</p>
                  <h3>{activeStep.title}</h3>
                  <p>{activeStep.body}</p>
                  <div className="clode-tour-copy__actions">
                    <button
                      type="button"
                      className="clode-button clode-button--quiet"
                      onClick={goToPreviousStep}
                      disabled={tourIndex === 0}
                    >
                      <i className="bi bi-arrow-left" aria-hidden="true" />
                      Back
                    </button>
                    <button type="button" className="clode-button clode-button--primary" onClick={goToNextStep}>
                      {tourIndex === tourSteps.length - 1 ? 'Finish' : 'Next'}
                      <i className="bi bi-arrow-right" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="clode-context">
                  <p className="clode-panel__eyebrow">Current page</p>
                  <h3>{pageContext.title}</h3>
                  <p>{pageContext.summary}</p>
                  <ul>
                    {pageContext.nextBestActions.map((action) => (
                      <li key={action}>
                        <i className="bi bi-check2" aria-hidden="true" />
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="clode-agent">
                <div className="clode-agent__header">
                  <div>
                    <p className="clode-panel__eyebrow">Clode Talk</p>
                    <h3>Ask the agent</h3>
                  </div>
                </div>

                <div className="clode-agent__messages" aria-live="polite">
                  {agentMessages.length === 0 ? (
                    <p className="clode-agent__empty">
                      Ask about cloud migration, cybersecurity, DevOps, managed services, or the current page.
                    </p>
                  ) : (
                    agentMessages.map((message, index) => (
                      <div
                        // Messages can repeat in short chats, so index is acceptable for this transient UI list.
                        key={`${message.role}-${index}`}
                        className={`clode-agent__message clode-agent__message--${message.role}`}
                      >
                        {message.content}
                      </div>
                    ))
                  )}
                  {isAgentThinking && (
                    <div className="clode-agent__message clode-agent__message--assistant">
                      Clode is checking the CloudYatra context...
                    </div>
                  )}
                </div>

                {agentError && (
                  <div className="clode-agent__error" role="alert">
                    {agentError}
                  </div>
                )}

                <form className="clode-agent__form" onSubmit={sendAgentMessage}>
                  <label className="sr-only" htmlFor="clode-agent-input">
                    Ask Clode
                  </label>
                  <input
                    id="clode-agent-input"
                    type="text"
                    value={agentInput}
                    onChange={(event) => setAgentInput(event.target.value)}
                    placeholder="Ask Clode about cloud or security..."
                    maxLength={600}
                    disabled={isAgentThinking}
                  />
                  <button
                    type="submit"
                    className="clode-icon-button clode-agent__send"
                    disabled={!agentInput.trim() || isAgentThinking}
                    aria-label="Send message to Clode"
                  >
                    <i className="bi bi-send" aria-hidden="true" />
                  </button>
                </form>
              </div>

              <div className="clode-action-grid">
                <button type="button" className="clode-action" onClick={startTour}>
                  <i className="bi bi-signpost-split" aria-hidden="true" />
                  Guide this page
                </button>
                <button type="button" className="clode-action" onClick={() => handleNavigate('/services/cloud-services')}>
                  <i className="bi bi-cloud" aria-hidden="true" />
                  Cloud services
                </button>
                <button type="button" className="clode-action" onClick={() => handleNavigate('/services/cybersecurity')}>
                  <i className="bi bi-shield-check" aria-hidden="true" />
                  Security review
                </button>
                <button type="button" className="clode-action" onClick={() => handleNavigate('/contact')}>
                  <i className="bi bi-calendar2-check" aria-hidden="true" />
                  Contact expert
                </button>
              </div>

              <footer className="clode-panel__footer">
                <i className={`bi ${isConfigured ? 'bi-check-circle-fill' : isConfigured === null ? 'bi-arrow-repeat' : 'bi-exclamation-circle'}`} aria-hidden="true" />
                <span>
                  {isConfigured === null && 'Connecting to Clode AI...'}
                  {isConfigured === true && 'Clode AI connected'}
                  {isConfigured === false && 'Clode AI is not available right now.'}
                </span>
              </footer>
            </motion.section>
          )}
        </AnimatePresence>

        <motion.button
          type="button"
          className={`clode-launcher ${isOpen && !isMinimized ? 'clode-launcher--active' : ''}`}
          onClick={() => {
            setIsMinimized(false);
            setIsOpen((open) => !open);
          }}
          whileHover={prefersReducedMotion ? undefined : { y: -2 }}
          whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
          aria-expanded={isOpen && !isMinimized}
          aria-label={isOpen && !isMinimized ? 'Close Clode assistant' : 'Open Clode assistant'}
        >
          <ClodeGlyph mode={mode} />
          <span className="clode-launcher__label">
            <span>Clode</span>
            <small>{isTouring ? 'Guiding' : 'Ask for guidance'}</small>
          </span>
        </motion.button>
      </motion.aside>
    </>
  );
};

export default ClodeAssistant;
